import { useEffect, useState } from "react";
import { Shield, Sparkles, Loader2, CheckCircle2, AlertCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { errorMessage } from "@/lib/errors";

/**
 * AdminWatermarkTab — bulk-apply the brand logo watermark to every
 * locally-stored photo in `gallery_media`. Shows live progress polling.
 */
export default function AdminWatermarkTab() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const loadStatus = async () => {
    try {
      const r = await api.get("/admin/watermark/status");
      setStatus(r.data);
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't load status"));
    }
  };

  useEffect(() => {
    loadStatus();
    // Poll every 4s while a job is running
    const id = setInterval(() => {
      if (status?.last_job?.status === "running") {
        setRefreshing(true);
        loadStatus().finally(() => setRefreshing(false));
      }
    }, 4000);
    return () => clearInterval(id);
  }, [status?.last_job?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const kickOff = async () => {
    if (!status || status.pending === 0) {
      toast.success("No photos need watermarking — all caught up");
      return;
    }
    if (!confirm(`Apply the brand watermark to ${status.pending} photo${status.pending === 1 ? "" : "s"}? This will modify each file in storage (about 1 sec per photo).`)) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/watermark/bulk-apply");
      toast.success(`Job started · ${r.data.expected} photos queued`);
      setTimeout(loadStatus, 800);
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't start job"));
    } finally { setBusy(false); }
  };

  const lastJob = status?.last_job;
  const isRunning = lastJob?.status === "running";
  const progressPct = lastJob && lastJob.expected
    ? Math.round((lastJob.processed / lastJob.expected) * 100)
    : 0;

  return (
    <div data-testid="admin-watermark-tab" className="space-y-6 max-w-4xl">
      <div>
        <p className="text-[10px] uppercase tracking-[0.25em] text-gold mb-1">Admin · Brand Watermark</p>
        <h2 className="font-heading text-2xl text-white flex items-center gap-2">
          <Shield className="text-gold" size={22} />
          Photo Watermark Manager
        </h2>
        <p className="text-white/45 text-sm mt-1 max-w-2xl">
          Stamp the AristocratsJA brand logo onto every photo in storage.
          New uploads are watermarked automatically — this panel only re-runs
          the job for photos uploaded before the watermark was enabled.
        </p>
      </div>

      {/* Status tiles */}
      {status && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="p-4 border border-white/10 rounded-md bg-black/30">
            <p className="text-[10px] uppercase tracking-wider text-white/45">Total local photos</p>
            <p className="text-3xl font-display text-white mt-1">{status.local_total}</p>
          </div>
          <div className="p-4 border border-emerald-400/30 rounded-md bg-emerald-500/[0.06]">
            <p className="text-[10px] uppercase tracking-wider text-emerald-300/80">Watermarked</p>
            <p className="text-3xl font-display text-emerald-300 mt-1">{status.watermarked}</p>
          </div>
          <div className="p-4 border border-gold/30 rounded-md bg-gold/[0.06]">
            <p className="text-[10px] uppercase tracking-wider text-gold">Pending</p>
            <p className="text-3xl font-display text-gold mt-1">{status.pending}</p>
          </div>
          <div className="p-4 border border-white/10 rounded-md bg-black/30" title="Photos hosted on external CDNs (Unsplash etc.) — can't be modified in place">
            <p className="text-[10px] uppercase tracking-wider text-white/45">External (skipped)</p>
            <p className="text-3xl font-display text-white/55 mt-1">{status.external_skipped}</p>
          </div>
        </div>
      )}

      {/* Run-job panel */}
      <div className="p-5 border border-gold/30 rounded-md bg-gradient-to-br from-gold/[0.05] to-transparent space-y-3">
        <div className="flex items-start gap-3">
          <Sparkles className="text-gold shrink-0 mt-1" size={20} />
          <div className="flex-1">
            <h3 className="text-base font-body text-white mb-1">Apply watermark to all existing photos</h3>
            <p className="text-[12px] text-white/55">
              Bottom-right corner · 90% opacity · 13% of photo width · sharp anti-aliased compositing.
            </p>
          </div>
        </div>

        {isRunning && (
          <div className="space-y-2" data-testid="watermark-progress">
            <div className="flex items-center justify-between text-[11px] text-white/70">
              <span>Processing… {lastJob.processed} of {lastJob.expected}</span>
              <span className="text-gold">{progressPct}%</span>
            </div>
            <div className="h-1.5 bg-black/40 rounded-full overflow-hidden">
              <div className="h-full bg-gold transition-all" style={{ width: `${progressPct}%` }} />
            </div>
          </div>
        )}

        {lastJob && lastJob.status === "complete" && (
          <div className="text-[11px] text-emerald-300/80 inline-flex items-center gap-1.5" data-testid="watermark-last-job">
            <CheckCircle2 size={12} />
            Last run · {lastJob.processed} stamped · {lastJob.failed} failed · {new Date(lastJob.finished_at).toLocaleString()}
          </div>
        )}

        <div className="flex gap-2 pt-1">
          <button
            data-testid="watermark-run-btn"
            onClick={kickOff}
            disabled={busy || isRunning || !status || status.pending === 0}
            className="inline-flex items-center gap-2 px-5 py-3 bg-gold text-luxury-black text-xs uppercase tracking-wider rounded font-medium hover:bg-gold/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {busy || isRunning
              ? <><Loader2 size={14} className="animate-spin" /> {isRunning ? "Watermarking…" : "Starting…"}</>
              : <><Sparkles size={14} /> Stamp {status?.pending || 0} pending photo{status?.pending === 1 ? "" : "s"}</>}
          </button>
          <button
            data-testid="watermark-refresh"
            onClick={() => { setRefreshing(true); loadStatus().finally(() => setRefreshing(false)); }}
            className="inline-flex items-center gap-1.5 px-3 py-3 border border-white/15 text-white/65 hover:text-white text-xs uppercase tracking-wider rounded transition-colors"
          >
            <RefreshCw size={12} className={refreshing ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {/* External photos disclaimer */}
      {status && status.external_skipped > 0 && (
        <div className="p-4 border border-amber-400/30 rounded-md bg-amber-500/[0.05] flex items-start gap-3">
          <AlertCircle size={16} className="text-amber-300 shrink-0 mt-0.5" />
          <div className="text-[11px] text-amber-100/80 leading-relaxed">
            <strong className="text-amber-300">{status.external_skipped} photo{status.external_skipped === 1 ? "" : "s"} hosted on external CDNs</strong> (Unsplash, etc.) can't be watermarked in place. To watermark those, re-upload them through the gallery — auto-watermark fires on every new upload.
          </div>
        </div>
      )}
    </div>
  );
}
